import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  Button,
  Input,
  Select,
  Option
} from '@mui/joy';
import { ChevronRight, ChevronLeft, NotebookPen, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'react-toastify';
import { authFetch } from '../../utils/authFetch';
import { generateChecklistPDF } from '../../utils/generateChecklistPDF';


// ...import come da te forniti...
export default function ChecklistPage() {
  const selectedOrg = localStorage.getItem('selectedOrg');
  const [checklists, setChecklists] = useState([]);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [filters, setFilters] = useState({});
  const perPage = 10;

  useEffect(() => {
    if (!selectedOrg) return;

    const fetchChecklists = async () => {
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/checklist/all`,
          { credentials: 'include' }
        );
        if (!res.ok) throw new Error('Errore nel fetch delle checklist');

        const data = await res.json();
        if (Array.isArray(data)) {
          const dataWithNote = data.map(item => {
            let noteFlag = 0;
            try {
              const category = JSON.parse(item.category);
              const noteObj = category.find(cat => cat.name === 'NOTE');
              if (noteObj && noteObj.stage === true) noteFlag = 1;
            } catch {}
            return { ...item, note: noteFlag };
          });
          setChecklists(dataWithNote);
        } else {
          toast.error('Risposta inattesa dal server checklist');
        }
      } catch (err) {
        console.error('Errore fetch checklist:', err);
        toast.error('Errore nel caricamento delle checklist');
      }
    };

    fetchChecklists();
  }, [selectedOrg]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return isNaN(d) ? '' : d.toLocaleDateString('it-IT');
    } catch {
      return '';
    }
  };

  const filteredChecklists = useMemo(() => {
    return checklists.filter((item) => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        if (key === 'status') return String(item[key]) === value;
        let fieldValue = item[key];
        if (key === 'start_date' || key === 'end_date') {
          fieldValue = formatDate(fieldValue);
        } else if (fieldValue == null) {
          fieldValue = '';
        } else {
          fieldValue = String(fieldValue);
        }
        return fieldValue.toLowerCase().includes(value.toLowerCase());
      });
    });
  }, [checklists, filters]);

  const sortedChecklists = useMemo(() => {
    if (!sortField) return filteredChecklists;
    return [...filteredChecklists].sort((a, b) => {
      const valA = a[sortField];
      const valB = b[sortField];
      if (valA == null) return 1;
      if (valB == null) return -1;
      return sortOrder === 'asc'
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
  }, [filteredChecklists, sortField, sortOrder]);

  const paginatedChecklists = useMemo(() => {
    const start = (page - 1) * perPage;
    return sortedChecklists.slice(start, start + perPage);
  }, [sortedChecklists, page]);

  const totalPages = Math.ceil(sortedChecklists.length / perPage);

  useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [totalPages, page]);

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'username', label: 'Utente' },
    { key: 'vehicle_name', label: 'Veicolo' },
    { key: 'status', label: 'Stato' },
    { key: 'start_date', label: 'Data inizio' },
    { key: 'end_date', label: 'Data fine' },
  ];

  return (
    <Box>
      <Typography level="h3" mb={2}>Checklist</Typography>

      <Box sx={{ borderRadius: 'lg', overflow: 'hidden', border: '1px solid', borderColor: 'neutral.outlinedBorder' }}>
        <Table
          size="sm"
          sx={{
            tableLayout: 'auto',
            '& thead th': {
              bgcolor: 'neutral.softBg',
              textAlign: 'left',
              px: 2,
              py: 1,
              fontSize: 'sm',
              cursor: 'pointer',
            },
            '& tbody td': {
              px: 2,
              py: 1,
              fontSize: 'md',
            },
            '& tbody tr:hover': {
              bgcolor: 'neutral.plainHoverBg',
              cursor: 'pointer',
            },
            '& td:nth-of-type(3), & td:nth-of-type(4), & td:nth-of-type(5), & td:nth-of-type(6), & th:nth-of-type(3), & th:nth-of-type(4), & th:nth-of-type(5), & th:nth-of-type(6)': {
              whiteSpace: 'nowrap',
              minWidth: 90,
              maxWidth: 140,
            },
            '& td:nth-of-type(1), & th:nth-of-type(1)': {
              whiteSpace: 'nowrap',
              width: '7%',
              maxWidth: 50,
              textAlign: 'left',
            },
            
          }}
        >
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} onClick={() => handleSort(col.key)}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {col.label}
                    {sortField === col.key ? (
                      sortOrder === 'asc' ? (
                        <ArrowUp size={14} />
                      ) : (
                        <ArrowDown size={14} />
                      )
                    ) : (
                      <ArrowDown size={14} color="#ccc" /> // icona grigia se non attivo
                    )}
                  </Box>
                </th>
              ))}
            </tr>
            <tr>
              {columns.map((col) => (
                <th key={col.key}>
                  {col.key === 'status' ? (
                    <Select
                      size="sm"
                      value={filters[col.key] || ''}
                      onChange={(_, newValue) => setFilters({ ...filters, [col.key]: newValue })}
                      placeholder="Tutti"
                      sx={{ width: '100%' }}
                    >
                      <Option value="">Tutti</Option>
                      <Option value="1">Aperta</Option>
                      <Option value="2">Chiusa</Option>
                    </Select>
                  ) : (
                    <Input
                      size="sm"
                      placeholder="Cerca"
                      value={filters[col.key] || ''}
                      onChange={(e) => setFilters({ ...filters, [col.key]: e.target.value })}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedChecklists.length > 0 ? (
              paginatedChecklists.map((item) => (
                <tr key={item.id} onClick={() => generateChecklistPDF(item.id)}>
                  <td>{item.id}</td>
                  <td>
                    {item.note === 1 && (
                      <NotebookPen size={16} style={{ marginRight: 6, verticalAlign: 'middle', color: '#1976d2' }} />
                    )}
                    {item.username}
                  </td>
                  <td>{item.vehicle_name}</td>
                  <td>
                    {item.status === 1 && (
                      <Box component="span" sx={{ backgroundColor: '#FFEB3B', color: '#000', px: 1, py: 0.25, borderRadius: 4, fontSize: '0.8rem', fontWeight: 'bold' }}>Aperta</Box>
                    )}
                    {item.status === 2 && (
                      <Box component="span" sx={{ backgroundColor: '#4CAF50', color: '#fff', px: 1, py: 0.25, borderRadius: 4, fontSize: '0.8rem', fontWeight: 'bold' }}>Chiusa</Box>
                    )}
                    {item.status !== 1 && item.status !== 2 && item.status}
                  </td>
                  <td>{formatDate(item.start_date)}</td>
                  <td>{formatDate(item.end_date)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: 'center', padding: '12px' }}>
                  Nessuna checklist trovata.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Button size="sm" variant="outlined" disabled={page === 1} onClick={() => setPage((p) => p - 1)} startDecorator={<ChevronLeft size={14} />}>
            Precedente
          </Button>
          <Typography level="body-sm">Pagina {page} di {totalPages}</Typography>
          <Button size="sm" variant="outlined" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} endDecorator={<ChevronRight size={14} />}>
            Successiva
          </Button>
        </Box>
      )}
    </Box>
  );
}
